import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, DropzoneDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

const WEAVE_TRANSITION_TIMING = '150ms cubic-bezier(0.2, 0, 0.2, 1)';

const composeWeaveTransition = (properties: string[]): string =>
  properties
    .map((property) => `${property} ${WEAVE_TRANSITION_TIMING}`)
    .join(', ');

export const getDropzoneTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): DropzoneDesignTokens => {
  const { tokens, typography, borderRadius, spacing, transitions } = theme;
  const { colors } = tokens;

  return {
    container: {
      _: {
        position: 'relative',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        'flex-direction': 'column',
        background: colors.background.primary.value,
        'border-radius': px(borderRadius.small),
        'border-width': '1px',
        'border-style': 'dashed',
        'border-color': colors.border.value,
        'padding-top': px(spacing.L),
        'padding-bottom': px(spacing.L),
        'padding-left': px(spacing.M),
        'padding-right': px(spacing.M),
        overflow: 'hidden',
        transition: transitions.create(['border-color', 'background-color'], {
          duration: 150,
        }),
      },
      idle: {
        '&:hover': {
          'border-color': colors.border.hover.dropzone.value,
        },
      },
      disabled: {
        true: {
          opacity: 0.35,
          cursor: 'not-allowed',
        },
        false: {},
      },
      dragActive: {
        true: {
          cursor: 'grabbing',
        },
        false: {
          cursor: 'pointer',
        },
      },
      size: {
        default: {},
        small: {
          height: '64px',
        },
      },
    },
    overlay: {
      _: {
        position: 'absolute',
        background: colors.background.secondary.value,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        'flex-direction': 'column',
        transition: transitions.create(['opacity'], { duration: 150 }),
      },
      dragActive: {
        true: {
          opacity: 1,
          'z-index': 1,
        },
        false: {
          opacity: 0,
          'z-index': -1,
        },
      },
    },
    'drag-active-text': {
      default: {
        'font-family': typography.heading2.fontFamily,
        'font-size': typography.heading2.fontSize,
        'font-weight': typography.heading2.fontWeight,
        'line-height': typography.heading2.lineHeight,
        color: colors.text.primary.value,
      },
      small: {
        'font-family': typography.heading3.fontFamily,
        'font-size': typography.heading3.fontSize,
        'font-weight': typography.heading3.fontWeight,
        'line-height': typography.heading3.lineHeight,
        color: colors.text.primary.value,
      },
    },
    'content-small': {
      'font-family': typography.heading3.fontFamily,
      'font-size': typography.heading3.fontSize,
      'font-weight': typography.heading3.fontWeight,
      'line-height': typography.heading3.lineHeight,
      color: colors.text.primary.value,
      'text-align': 'center',
    },
    'content-description': {
      color: colors.text.secondary.value,
    },
  };
};

export const getDropzoneTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): DropzoneDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;

  const spacing16 = generic?.spacing?.[16];
  const spacing24 = generic?.spacing?.[24];
  const surfaceBackground = semantic?.['background-color']?.surface?.[100];

  return {
    container: {
      _: {
        position: 'relative',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        'flex-direction': 'column',
        background: surfaceBackground,
        'border-radius': generic?.['border-radius']?.[2],
        'border-width': generic?.['border-width']?.[1],
        'border-style': 'dashed',
        'border-color': semantic?.['border-color']?.medium?.default,
        'padding-top': spacing24,
        'padding-bottom': spacing24,
        'padding-left': spacing16,
        'padding-right': spacing16,
        overflow: 'hidden',
        transition: composeWeaveTransition([
          'border-color',
          'background-color',
        ]),
      },
      idle: {
        '&:hover': {
          'border-color': semantic?.['border-color']?.accent?.default,
        },
      },
      disabled: {
        true: {
          opacity: semantic?.opacity?.disabled,
          cursor: 'not-allowed',
        },
        false: {},
      },
      dragActive: {
        true: {
          cursor: 'grabbing',
        },
        false: {
          cursor: 'pointer',
        },
      },
      size: {
        default: {},
        small: {
          height: generic?.size?.[64],
        },
      },
    },
    overlay: {
      _: {
        position: 'absolute',
        background: semantic?.['background-color']?.surface?.[200],
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        'flex-direction': 'column',
        transition: composeWeaveTransition(['opacity']),
      },
      dragActive: {
        true: {
          opacity: 1,
          'z-index': 1,
        },
        false: {
          opacity: 0,
          'z-index': -1,
        },
      },
    },
    'drag-active-text': {
      default: {
        'font-family': fontFamily,
        'font-size': generic?.['font-size']?.[20],
        'font-weight': '400',
        'line-height': generic?.['line-height']?.[26],
        color: semantic?.['text-color']?.medium?.default,
      },
      small: {
        'font-family': fontFamily,
        'font-size': generic?.['font-size']?.[16],
        'font-weight': '400',
        'line-height': generic?.['line-height']?.[22],
        color: semantic?.['text-color']?.medium?.default,
      },
    },
    'content-small': {
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[16],
      'font-weight': '400',
      'line-height': generic?.['line-height']?.[22],
      color: semantic?.['text-color']?.medium?.default,
      'text-align': 'center',
    },
    'content-description': {
      color: semantic?.['text-color']?.light,
    },
  };
};
